import { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { Form, InputNumber } from "antd";

import FormInput from "./FormInput";
import FormSelect from "./FormSelect";
import FormDatePicker from "./FormDatePicker";
import FormCheckbox from "./FormCheckbox";
import FormEmail from "./FormEmail";
import FormButton from "./FormButton";
import FormTextArea from "./FormTextArea";
import FormFileInput from "./FormFileInput";
import MultiSelect from "./MultiSelect";


const FormComponent = forwardRef(({
  title,
  formItems,
  layout,
  className,
  handleSubmit,
  defaultValues = {},
  loginError = null
}, ref) => {
  const [form] = Form.useForm();
  const [resetTrigger, setResetTrigger] = useState(false);

  // Prefill form values when defaultValues change
  useEffect(() => {
    if (defaultValues) {
      form.setFieldsValue(defaultValues);
    }
  }, [defaultValues, form]);

  // Expose method to parent component
  useImperativeHandle(ref, () => ({
    validateAndReturnValues: async () => {
      const values = await form.validateFields();
      return values;
    }
  }));

  const handleOk = async () => {
    try {
      const values = await form.validateFields();

      if (values.upload) {
        values.files = values.upload.fileList;
        delete values.upload;
        setResetTrigger(!resetTrigger);
      }
  
      const result = await handleSubmit(values); 
  
      if (result?.success) {
        form.resetFields(); // Only reset on success
      }
    } catch (info) {
      console.log("Validation Failed:", info);
    }
  };

  return (
    <>
      {title && <h1 className="form-title">{title}</h1>}
      <Form
        form={form}
        autoComplete="off"
        layout={layout}
        className={className}
        initialValues={defaultValues}
      >
        {formItems.map((item, index) => {
          if (item.component) {
            const CustomComponent = item.component;
            return <CustomComponent key={index} field={item} form={form} />;
          }

          switch (item.type) {
             case "text":
            case "number":
            case "password":
              return <FormInput key={index} {...item} />;
            case "email":
              return <FormEmail key={index} {...item} />;
            case "select":
              return <FormSelect key={index} {...item} />;
            case "multiselect":
              return <MultiSelect key={index} {...item} />;
            case "datePicker":
              return (
                <FormDatePicker
                  key={index}
                  {...item}
                  value={form.getFieldValue(item.name)}
                  onChange={(value) => form.setFieldsValue({ [item.name]: value })}
                />
              );
            case "checkbox":
              return <FormCheckbox key={index} {...item} />;
            case "textArea":
              return <FormTextArea key={index} {...item} />;
            case "fileUpload":
              return (
                <FormFileInput
                  key={index}
                  {...item}
                  resetTrigger={resetTrigger}
                />
              );
            case "submit":
              return (
                <div key={index}>
                  {loginError && <p className="text-danger mb-0">Email or password is incorrect.</p>}
                  <FormButton {...item} onClick={handleOk} />
                </div>
              );
            case "submitreset":
              return (
                <div key={index} className="d-flex justify-content-end">
                  <FormButton
                    label={item.resetlabel}
                    key={index}
                    {...item}
                    loading={false}
                    onClick={() => form.resetFields()}
                  />
                  <FormButton
                    label={item.submitlabel}
                    buttonType="primary"
                    key={index}
                    {...item}
                    loading={item.loading}
                    onClick={handleOk}
                  />
                </div>
              );
            case "custom":
              const CustomComponent = item.component;
              return <CustomComponent key={index} field={item} form={form} />;
            default:
              return null;
          }
        })}

      </Form>
    </>
  );
});

export default FormComponent;